import React, { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '../../utils/apiUtils';
import './RewardsPage.css';

interface Prize {
  id: string; title: string; description?: string;
  costPoints: number; affordable: boolean; distanceMeters: number | null;
  partner: { id: string; name: string };
}
interface Pending {
  redemption: { id: string; expiresAt: string; costPoints: number };
  prize: { id: string; title: string };
}

const RewardsPage: React.FC = () => {
  const [balance, setBalance] = useState(0);
  const [expiringSoon, setExpiringSoon] = useState(0);
  const [nextExpiry, setNextExpiry] = useState<string | null>(null);
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [pending, setPending] = useState<Pending | null>(null);
  const [pin, setPin] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const [me, catalog] = await Promise.all([
      apiRequest<any>('/api/rewards/me'),
      apiRequest<any>('/api/rewards/catalog'),
    ]);
    if (me.data) {
      setBalance(me.data.balance);
      setExpiringSoon(me.data.expiringSoon);
      setNextExpiry(me.data.nextExpiry);
    }
    if (catalog.data) setPrizes(catalog.data.prizes || []);
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (!pending) return;
    const target = new Date(pending.redemption.expiresAt).getTime();
    const tick = () => {
      const left = Math.max(0, Math.round((target - Date.now()) / 1000));
      setSecondsLeft(left);
      // Al vencer, la reserva se cae sola y los puntos vuelven.
      if (left === 0) { setPending(null); reload(); }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [pending, reload]);

  const redeem = async (prizeId: string) => {
    setError(null);
    const res = await apiRequest<any>(`/api/rewards/prizes/${prizeId}/redeem`, { method: 'POST', body: {} });
    if (res.error || !res.data) { setError(res.error || 'No pudimos reservar el premio'); return; }
    setPin('');
    setPending({ redemption: res.data.redemption, prize: res.data.prize });
  };

  const confirm = async () => {
    if (!pending) return;
    setError(null);
    const res = await apiRequest<any>(`/api/rewards/redemptions/${pending.redemption.id}/confirm`, {
      method: 'POST', body: { pin },
    });
    // Un código mal tipeado no debería costarle el premio al jugador: el
    // canje sigue vivo y se puede reintentar.
    if (res.error || !res.data) { setError(res.error || 'No pudimos confirmar el canje'); return; }
    setPending(null);
    reload();
  };

  if (loading) return <div className="rewards-player">Cargando...</div>;

  const mmss = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

  return (
    <div className="rewards-player">
      <p className="rewards-balance-label">Mis puntos</p>
      <p className="rewards-balance">{balance}</p>
      {expiringSoon > 0 && nextExpiry && (
        <p className="rewards-expiring">
          Vencen {expiringSoon} puntos el {new Date(nextExpiry).toLocaleDateString('es-AR')}
        </p>
      )}
      {balance === 0 && (
        <p className="rewards-note">
          Todavía no tienes puntos. Juega un partido y valídalo en la cancha.
        </p>
      )}
      {error && <div className="rewards-player-error">{error}</div>}

      <h2 className="rewards-section-title">Premios cerca tuyo</h2>
      {prizes.length === 0 ? (
        <p className="rewards-note">Todavía no hay premios en tu zona.</p>
      ) : (
        prizes.map((p) => (
          <div className="rewards-prize" key={p.id}>
            <div>
              <p className="rewards-prize-title">{p.title}</p>
              <p className="rewards-prize-partner">{p.partner.name}</p>
              {p.distanceMeters !== null && (
                <p className="rewards-prize-distance">a {p.distanceMeters} m</p>
              )}
            </div>
            <div className="rewards-prize-side">
              <p className="rewards-prize-cost">{p.costPoints} puntos</p>
              {p.affordable ? (
                <button className="rewards-redeem" onClick={() => redeem(p.id)}>Canjear</button>
              ) : (
                /* Decir cuánto falta, no solo que no se puede. */
                <p className="rewards-prize-missing">Te faltan {p.costPoints - balance} puntos</p>
              )}
            </div>
          </div>
        ))
      )}

      {pending && (
        <div className="rewards-modal-backdrop">
          <div className="rewards-modal">
            <p className="rewards-modal-prize">{pending.prize.title}</p>
            <p className="rewards-modal-title">Muéstrale esto a quien te atiende</p>
            <p className="rewards-modal-instruction">Quien te atiende escribe su código de 4 números</p>
            <input
              className="rewards-pin"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              inputMode="numeric"
              maxLength={4}
              placeholder="····"
              aria-label="Código del local"
              autoFocus
            />
            <p className="rewards-modal-countdown">Se cancela en {mmss}</p>
            <button className="rewards-modal-confirm" onClick={confirm} disabled={pin.length !== 4}>
              Confirmar
            </button>
            <button className="rewards-modal-cancel" onClick={() => { setPending(null); reload(); }}>
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RewardsPage;
